import { integer, uuid, varchar, jsonb, bigint, boolean, timestamp } from 'drizzle-orm/pg-core/columns';
import { pgTable } from 'drizzle-orm/pg-core/table';
import { index } from 'drizzle-orm/pg-core/indexes';
import { foreignKey } from 'drizzle-orm/pg-core/foreign-keys';
import { check } from 'drizzle-orm/pg-core/checks';
import { companies } from './companies';
import { sql } from 'drizzle-orm/sql/sql';
import { InferSelectModel } from 'drizzle-orm/table';
import { byteaAsBuffer } from './types-bytea';
import { commonFieldsWithSoftDelete } from './common-fields';

export type BolagoFile = InferSelectModel<typeof files>;

export enum FileGenState {
  Completed,
  Scheduled,
  Waiting,
  Failed,
  InProgress,
  NotApplicable,
}

export enum FileSignatureMethod {
  Analogue = 'Analogue',
  Digital = 'Digital',
}

export enum FileMimeType {
  Folder = 'application/x.folder',
  Pdf = 'application/pdf',
}

export enum FileSystemTagKind {
  // also exist in WarrantParticipantDocumentKind
  EmployeeOptionsAgreement = 'EmployeeOptionsAgreement',
  WarrantAgreement = 'WarrantAgreement',
  WiseAgreement = 'WiseAgreement',
  NiceAgreement = 'NiceAgreement',
  MeetingAppendix = 'MeetingAppendix',
  EmployeeOptionsSummary = 'EmployeeOptionsSummary',
  EmployeeOptionsShareholdersAgreement = 'EmployeeOptionsShareholdersAgreement',

  MeetingProtocol = 'MeetingProtocol',
  MeetingSummons = 'MeetingSummons',
  MeetingVotingList = 'MeetingVotingList',
  MeetingSummonsCoverSheet = 'MeetingSummonsCoverSheet',

  AppendixShareIssue = 'AppendixShareIssue',
  ShareSubscriptionList = 'ShareSubscriptionList',
  ShareIssueRegistrationForm = 'ShareIssueRegistrationForm',

  BoardsStatement = 'BoardsStatement',
  BoardsStatementOffset = 'BoardsStatementOffset',
  BoardsStatementNonCash = 'BoardsStatementNonCash',
  AuditorsStatement = 'AuditorsStatement',
  AuditorsStatementOffset = 'AuditorsStatementOffset',
  AuditorsStatementNonCash = 'AuditorsStatementNonCash',

  PowerOfAttorney = 'PowerOfAttorney',

  TransactionNote = 'TransactionNote',

  AppendixWarrantValuation = 'AppendixWarrantValuation',
  AppendixWarrantTerms = 'AppendixWarrantTerms',
  AppendixWarrantIssueDecision = 'AppendixWarrantIssueDecision',
  ShareholderWaiver = 'ShareholderWaiver',
  WarrantCertificate = 'WarrantCertificate',
  WarrantTodoList = 'WarrantTodoList',
  WarrantRegistrationForm = 'WarrantRegistrationForm',

  // Wise
  AppendixWiseTerms = 'AppendixWiseTerms',

  AppendixNiceTerms = 'AppendixNiceTerms',
  ConvertibleRegistrationForm = 'ConvertibleRegistrationForm',

  ArticlesOfAssoc = 'ArticlesOfAssoc',
  ArticlesOfAssocRegForm = 'ArticlesOfAssocRegForm',

  AppendixAuthShareIssue = 'AppendixAuthShareIssue',
  AppendixAuthConvertibleIssue = 'AppendixAuthConvertibleIssue',
  AppendixAuthWarrantIssue = 'AppendixAuthWarrantIssue',
  AuthorizationRegistrationForm = 'AuthorizationRegistrationForm',

  OneWayNda = 'OneWayNda',
  EmploymentAgreement = 'EmploymentAgreement',
  Agreement = 'Agreement',

  // Non-generated

  Folder = 'Folder',
  Other = 'Other', // user uploaded, can be any document.
  // only uploaded for now
  ShareholderAgreement = 'ShareholderAgreement',
  AnnualReport = 'AnnualReport',
}

export const isFileKindGenerated: Record<FileSystemTagKind, boolean> = {
  [FileSystemTagKind.EmployeeOptionsAgreement]: true,
  [FileSystemTagKind.WarrantAgreement]: true,
  [FileSystemTagKind.WiseAgreement]: true,
  [FileSystemTagKind.NiceAgreement]: true,
  [FileSystemTagKind.MeetingAppendix]: true,
  [FileSystemTagKind.EmployeeOptionsSummary]: true,
  [FileSystemTagKind.EmployeeOptionsShareholdersAgreement]: true,
  [FileSystemTagKind.MeetingProtocol]: true,
  [FileSystemTagKind.MeetingSummons]: true,
  [FileSystemTagKind.MeetingVotingList]: true,
  [FileSystemTagKind.MeetingSummonsCoverSheet]: true,
  [FileSystemTagKind.AppendixShareIssue]: true,
  [FileSystemTagKind.ShareSubscriptionList]: true,
  [FileSystemTagKind.ShareIssueRegistrationForm]: true,
  [FileSystemTagKind.BoardsStatement]: true,
  [FileSystemTagKind.BoardsStatementOffset]: true,
  [FileSystemTagKind.BoardsStatementNonCash]: true,
  [FileSystemTagKind.AuditorsStatement]: true,
  [FileSystemTagKind.AuditorsStatementOffset]: true,
  [FileSystemTagKind.AuditorsStatementNonCash]: true,
  [FileSystemTagKind.PowerOfAttorney]: true,
  [FileSystemTagKind.TransactionNote]: true,
  [FileSystemTagKind.AppendixWarrantValuation]: true,
  [FileSystemTagKind.AppendixWarrantTerms]: true,
  [FileSystemTagKind.AppendixWarrantIssueDecision]: true,
  [FileSystemTagKind.ShareholderWaiver]: true,
  [FileSystemTagKind.WarrantCertificate]: true,
  [FileSystemTagKind.WarrantTodoList]: true,
  [FileSystemTagKind.WarrantRegistrationForm]: true,
  [FileSystemTagKind.AppendixWiseTerms]: true,
  [FileSystemTagKind.AppendixNiceTerms]: true,
  [FileSystemTagKind.ConvertibleRegistrationForm]: true,
  [FileSystemTagKind.ArticlesOfAssoc]: true,
  [FileSystemTagKind.ArticlesOfAssocRegForm]: true,
  [FileSystemTagKind.AppendixAuthShareIssue]: true,
  [FileSystemTagKind.AppendixAuthConvertibleIssue]: true,
  [FileSystemTagKind.AppendixAuthWarrantIssue]: true,
  [FileSystemTagKind.AuthorizationRegistrationForm]: true,
  [FileSystemTagKind.OneWayNda]: true,
  [FileSystemTagKind.EmploymentAgreement]: true,
  [FileSystemTagKind.Agreement]: true,
  [FileSystemTagKind.Folder]: false,
  [FileSystemTagKind.Other]: false,
  [FileSystemTagKind.ShareholderAgreement]: false,
  [FileSystemTagKind.AnnualReport]: false,
};

export enum FileFolderTag {
  Employment = 'Employment',
  Shareholder = 'Shareholder',
  Board = 'Board',
  AnnualReports = 'AnnualReports',
  Aoa = 'Aoa',
  ShareholderMeetings = 'ShareholderMeetings',
  Nda = 'Nda',
  Poa = 'Poa',
  Qeso = 'Qeso',
  Warrants = 'Warrants',
  Subscription = 'Subscription',
  Registration = 'Registration',
}

export const fileFolderFields = pgTable('file_folder_fields', {
  id: uuid().defaultRandom().primaryKey().notNull(),
  kinds: varchar().array(),
  meetingKinds: varchar('meeting_kinds').array(),
  userTags: varchar('user_tags').array(),
  tag: varchar('tag').$type<FileFolderTag>().notNull(),
});

export const files = pgTable(
  'files',
  {
    ...commonFieldsWithSoftDelete,
    companyId: uuid().notNull(),
    userTags: jsonb(),
    mimeType: varchar().notNull().$type<FileMimeType>(),
    filename: varchar().notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    filesize: bigint({ mode: 'number' }),
    sha256: varchar(),
    document: byteaAsBuffer(),
    scriveId: varchar(),
    signedDocument: byteaAsBuffer(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    signedFilesize: bigint({ mode: 'number' }),
    signedSha256: varchar(),
    thumbnail: byteaAsBuffer(),
    signedThumbnail: byteaAsBuffer(),
    scriveAccount: varchar(),
    // scriveJson: jsonb(), not used
    dirty: boolean().default(false).notNull(),
    genState: integer().$type<FileGenState>().notNull(),
    scriveMtime: timestamp(),
    scriveStatus: varchar(),
    scriveCtime: timestamp(),
    uploaded: boolean().notNull(),
    kind: varchar().notNull().$type<FileSystemTagKind>(),
    groupId: varchar(),
    zignedAgreementId: varchar(),
    zignedAgreementStatus: varchar(),
    signatureMethod: varchar().$type<FileSignatureMethod>(),
    reminderSentAt: timestamp(),
    hasThumbnail: boolean().generatedAlwaysAs(sql`(thumbnail IS NOT NULL)`),
    parentId: uuid(),
    filenameSecondaryLang: varchar(),
    sharedWithShareholders: boolean('shared_with_shareholders').notNull().default(false),
  },
  table => {
    return [
      {
        indexDocumentsOnCompanyId: index('index_documents_on_company_id').using(
          'btree',
          table.companyId.asc().nullsLast().op('uuid_ops'),
        ),
        fkRails83141D4C8C: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'fk_rails_83141d4c8c',
        }),
        filesParentIdFkey: foreignKey({
          columns: [table.parentId],
          foreignColumns: [table.id],
          name: 'files_parent_id_fkey',
        }),

        chkScriveIdAndAccount: check(
          'chk_scrive_id_and_account',
          sql`((scrive_account IS NOT NULL) AND (scrive_id IS NOT NULL)) OR ((scrive_account IS NULL) AND (scrive_id IS NULL))`,
        ),
      },
    ];
  },
);
